import React, { useState, useEffect } from "react";
import "../styles/MyProfile.css";
import { useLocation } from "react-router-dom";
import { getJson } from "../network/api";
import { ApiEndpoints } from "../network/config";

const calculateAge = (dobString) => {
  if (!dobString) return "—";

  // Convert DOB into a valid Date object
  const dob = new Date(dobString);

  if (isNaN(dob.getTime())) return "—"; // invalid date handling

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();

  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  // Deduct age by 1 if birthday hasn't come yet this year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
};


const display = (value) => {
  if (!value && value !== 0) return "—";
  return value;
};

const SelectedProfileInfo = () => {
  const location = useLocation();

  // profileId comes from: navigation → state → selected profile list
  const routedProfile = location.state?.profile;
  const profileId =
    location.state?.profileId ||
    routedProfile?.id ||
    routedProfile?.profileId ||
    location.state?.id;

  const [activeTab, setActiveTab] = useState("basic");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [profileData, setProfileData] = useState(null);

  // Fetch profile details
  useEffect(() => {
    if (!profileId) return;

    const loadProfileInfo = async () => {
      try {
        const response = await getJson(
          `${ApiEndpoints.findByProfileId}/${profileId}`
        );
        setProfileData(response.data);
      } catch (error) {
        console.error("Error loading profile info:", error);
      }
    };

    loadProfileInfo();
  }, [profileId]);

  if (!profileId) {
    return <h3 style={{ textAlign: "center", marginTop: "40px" }}>No profile selected.</h3>;
  }

  if (!profileData) {
    return <h3 style={{ textAlign: "center", marginTop: "90px" }}>Loading...</h3>;
  }

  // IMAGE LIST
  const images = profileData.profileImages?.map((img) => img.imageUrl) || [];

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  return (
    <div className="my-profile-container">
      <h1 className="my-profile-title">Selected Profile</h1>

      <div className="my-profile-content">
        {/* LEFT SIDE */}
        <div className="my-profile-left">
          <h2 className="my-name">
            {profileData.name || profileData.personalDetails?.name || "Profile"}

          </h2>

          <div className="my-image-slider">
            {images.length > 0 ? (
              <img
                src={images[currentImageIndex]}
                alt="Profile"
                className="my-profile-image"
                onError={(e) => (e.target.style.display = "none")}
              />
            ) : (
              <div className="no-image-box">No Image Available</div>
            )}

            {images.length > 1 && (
              <>
                <button className="my-slider-btn my-prev-btn" onClick={prevImage}>
                  ‹
                </button>
                <button className="my-slider-btn my-next-btn" onClick={nextImage}>
                  ›
                </button>
              </>
            )}

            {images.length > 1 && (
              <div className="my-image-indicators">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`my-indicator ${
                      index === currentImageIndex ? "my-active-indicator" : ""
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="my-profile-right">
          <div className="my-tabs">
            {["basic","personal","birth","family","contact","education","looking"].map((tab) => (
              <button
                key={tab}
                className={`my-tab ${activeTab === tab ? "my-active-tab" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "looking" ? "Looking For" : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* ----- BASIC DETAILS ----- */}
          {activeTab === "basic" && (
            <div className="my-personal-details">
              <h3>Basic Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row"><span>Name</span><span>{display(profileData.name || profileData.personalDetails?.name)}</span></div>
                <div className="my-detail-row"><span>Gender</span><span>{display(profileData.gender)}</span></div>
                <div className="my-detail-row"><span>Marital Status</span><span>{display(profileData.personalDetails?.maritalStatus)}</span></div>
                <div className="my-detail-row"><span>Age</span><span>{calculateAge(profileData.birthDetails?.Dob)}</span></div>
                <div className="my-detail-row"><span>Mobile Number</span><span>{display(profileData.contactDetails?.mobileNo1)}</span></div>
                <div className="my-detail-row"><span>Caste</span><span>{display(profileData.caste)}</span></div>
                <div className="my-detail-row"><span>Subcaste</span><span>{display(profileData.subcaste)}</span></div>
                <div className="my-detail-row"><span>Bedagu</span><span>{display(profileData.bedagu)}</span></div>
              </div>
            </div>
          )}

          {/* PERSONAL */}
          {activeTab === "personal" && (
            <div className="my-personal-details">
              <h3>Personal Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row"><span>Height</span><span>{display(profileData.personalDetails?.height)}</span></div>
                <div className="my-detail-row"><span>Weight</span><span>{display(profileData.personalDetails?.weight)}</span></div>
                <div className="my-detail-row"><span>Blood Group</span><span>{display(profileData.personalDetails?.bloodGroup)}</span></div>
                <div className="my-detail-row"><span>Body Type</span><span>{display(profileData.personalDetails?.bodyType)}</span></div>
                <div className="my-detail-row"><span>Complexion</span><span>{display(profileData.personalDetails?.complexion)}</span></div>
              </div>
            </div>
          )}

          {/* BIRTH */}
          {activeTab === "birth" && (
            <div className="my-birth-details">
              <h3>Birth Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row"><span>Date of Birth</span><span>{display(profileData.birthDetails?.Dob)}</span></div>
                <div className="my-detail-row"><span>Birth Place</span><span>{display(profileData.birthDetails?.birthPlace)}</span></div>
                <div className="my-detail-row"><span>Birth Time</span><span>{display(profileData.birthDetails?.birthTime)}</span></div>
                <div className="my-detail-row"><span>Moon Sign</span><span>{display(profileData.birthDetails?.moonsign)}</span></div>
                <div className="my-detail-row"><span>Star</span><span>{display(profileData.birthDetails?.star)}</span></div>
                <div className="my-detail-row"><span>Gothra</span><span>{display(profileData.birthDetails?.gothra)}</span></div>
                <div className="my-detail-row"><span>Pada</span><span>{display(profileData.birthDetails?.pada)}</span></div>
              </div>
            </div>
          )}

          {/* FAMILY */}
          {activeTab === "family" && (
            <div className="my-family-details">
              <h3>Family Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row"><span>Father</span><span>{display(profileData.familyDetails?.fatherName)}</span></div>
                <div className="my-detail-row"><span>Father Alive</span><span>{display(profileData.familyDetails?.fatherAlive)}</span></div>
                <div className="my-detail-row"><span>Mother</span><span>{display(profileData.familyDetails?.motherName)}</span></div>
                <div className="my-detail-row"><span>Mother Occupation</span><span>{display(profileData.familyDetails?.motherOccupation)}</span></div>
                <div className="my-detail-row"><span>Brothers</span><span>{display(profileData.familyDetails?.noOfBrothers)}</span></div>
                <div className="my-detail-row"><span>Married Brothers</span><span>{display(profileData.familyDetails?.noOfBrothersMarried)}</span></div>
                <div className="my-detail-row"><span>Sisters</span><span>{display(profileData.familyDetails?.noOfSisters)}</span></div>
                <div className="my-detail-row"><span>Married Sisters</span><span>{display(profileData.familyDetails?.noOfSistersMarried)}</span></div>
              </div>
            </div>
          )}

          {/* CONTACT */}
          {activeTab === "contact" && (
            <div>
              <h3>Contact Details</h3>
              <div className="my-details-list">

                <div className="my-detail-row"><span>District</span><span>{display(profileData.contactDetails?.district)}</span></div>
                <div className="my-detail-row"><span>Resident At</span><span>{display(profileData.contactDetails?.residentAt)}</span></div>
                <div className="my-detail-row"><span>Contact No</span><span>{display(profileData.contactDetails?.mobileNo1)}</span></div>
              </div>
            </div>
          )}

          {/* EDUCATION */}
          {activeTab === "education" && (
            <div className="my-education-details">
              <h3>Education & Profession</h3>
              <div className="my-details-list">
                <div className="my-detail-row"><span>Education Stream</span><span>{display(profileData.educationsDetails?.educationStream)}</span></div>
                <div className="my-detail-row"><span>Qualification</span><span>{display(profileData.educationsDetails?.qualification)}</span></div>
                <div className="my-detail-row"><span>Occupation</span><span>{display(profileData.educationsDetails?.occupation)}</span></div>
                <div className="my-detail-row"><span>Working In</span><span>{display(profileData.educationsDetails?.workingIn)}</span></div>
                <div className="my-detail-row"><span>Annual Income</span><span>{display(profileData.educationsDetails?.annualIncome)}</span></div>
                <div className="my-detail-row"><span>Job Type</span><span>{display(profileData.educationsDetails?.jobType)}</span></div>
              </div>
            </div>
          )}

          {/* LOOKING FOR */}
          {activeTab === "looking" && (
            <div className="my-lookin-details">
              <h3>Looking For</h3>
              <div className="my-details-list">
                <div className="my-detail-row"><span>Age</span><span>{display(profileData.lookingForDetails?.ageFrom)} to {display(profileData.lookingForDetails?.ageTo)}</span></div>
                <div className="my-detail-row"><span>Caste</span><span>{display(profileData.lookingForDetails?.caste)}</span></div>
                <div className="my-detail-row"><span>Subcaste</span><span>{display(profileData.lookingForDetails?.subCaste)}</span></div>
                <div className="my-detail-row"><span>Height</span><span>{display(profileData.lookingForDetails?.height)}</span></div>
                <div className="my-detail-row"><span>Qualification</span><span>{display(profileData.lookingForDetails?.qualification)}</span></div>
                <div className="my-detail-row"><span>Occupation</span><span>{display(profileData.lookingForDetails?.occupation)}</span></div>
                <div className="my-detail-row"><span>Annual Income</span><span>{display(profileData.lookingForDetails?.annualIncome)}</span></div>
                <div className="my-detail-row"><span>Marital Status</span><span>{display(profileData.lookingForDetails?.maritalStatus)}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedProfileInfo;
